import { Bot, ExternalLink, Eye, EyeOff, Loader2, User, X } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import type {
  LinkedTaskSchema,
  TaskUpdateSchema,
} from '@/services/api/generated/externalTaskService';

interface LinkedTasksListProps {
  linkedTasks: LinkedTaskSchema[];
  externalTaskId: string;
  onUnlink: (linkId: string) => Promise<void>;
  isUnlinking: boolean;
  onTaskClick?: (taskId: string) => void;
  selectedTaskId?: string | null;
}

export const LinkedTasksList: React.FC<LinkedTasksListProps> = ({
  linkedTasks,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  externalTaskId,
  onUnlink,
  isUnlinking,
  onTaskClick,
  selectedTaskId,
}) => {
  const [unlinkingId, setUnlinkingId] = React.useState<string | null>(null);

  const handleUnlink = async (linkId: string) => {
    setUnlinkingId(linkId);
    try {
      await onUnlink(linkId);
    } finally {
      setUnlinkingId(null);
    }
  };

  if (linkedTasks.length === 0) {
    return (
      <div className="border border-dashed border-slate-300 rounded-lg p-4">
        <p className="text-sm text-slate-500 text-center">
          No linked tasks yet
        </p>
      </div>
    );
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const truncateText = (text: string | null | undefined, maxLength = 120) => {
    if (!text) return null;
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };

  const countUpdatesLast24Hours = (updates: TaskUpdateSchema[]) => {
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    return updates.filter((update) => {
      const updateDate = new Date(update.created_at);
      return updateDate >= twentyFourHoursAgo;
    }).length;
  };

  return (
    <div className="space-y-2">
      {linkedTasks.map((linkedTask) => {
        const isSelected = selectedTaskId === linkedTask.id;
        const updatesCount = countUpdatesLast24Hours(linkedTask.updates);
        return (
          <div
            key={linkedTask.id}
            className={`p-3 border rounded-lg transition-colors ${
              isSelected
                ? 'border-blue-500 bg-blue-50'
                : 'border-slate-200 bg-white hover:bg-slate-50'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0 space-y-2">
                {/* Top line: Name, Status, Due Date */}
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {linkedTask.name}
                  </div>
                  <Badge color="zinc" className="text-xs shrink-0">
                    {linkedTask.status_name}
                  </Badge>
                  <span className="text-xs text-slate-500 shrink-0">
                    {linkedTask.due_date
                      ? `Due ${formatDate(linkedTask.due_date)}`
                      : 'No due date'}
                  </span>
                  {updatesCount > 0 && (
                    <>
                      <span className="text-slate-400">•</span>
                      <span className="text-xs text-blue-600 font-medium shrink-0">
                        {updatesCount} update{updatesCount !== 1 ? 's' : ''}{' '}
                        (24h)
                      </span>
                    </>
                  )}
                  {/* Link source indicator */}
                  <span className="text-slate-400">•</span>
                  <span className="text-xs text-slate-400 flex items-center gap-1 shrink-0">
                    {linkedTask.link_source === 'AUTO_RULE' ? (
                      <>
                        <Bot className="h-3 w-3" />
                        <span>Auto-linked</span>
                      </>
                    ) : (
                      <>
                        <User className="h-3 w-3" />
                        <span>Manual</span>
                      </>
                    )}
                  </span>
                </div>

                {/* Body: Truncated description (last note) */}
                {linkedTask.description && (
                  <div className="text-xs text-slate-600 line-clamp-2">
                    {truncateText(linkedTask.description)}
                  </div>
                )}
              </div>
              <div className="flex items-start gap-1">
                {onTaskClick ? (
                  <Button
                    plain
                    onClick={() => onTaskClick(linkedTask.id)}
                    className={`h-auto p-1.5 transition-colors ${
                      isSelected
                        ? 'text-blue-700 bg-blue-100 hover:bg-blue-200'
                        : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
                    }`}
                    title={
                      isSelected ? 'Close task details' : 'View task details'
                    }
                  >
                    {isSelected ? (
                      <EyeOff className="h-3.5 w-3.5" />
                    ) : (
                      <Eye className="h-3.5 w-3.5" />
                    )}
                  </Button>
                ) : (
                  <Button
                    plain
                    onClick={() => {
                      window.open(
                        `/tasks/${linkedTask.id}`,
                        '_blank',
                        'noopener,noreferrer',
                      );
                    }}
                    className="h-auto p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    title="Open task"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  plain
                  onClick={() => handleUnlink(linkedTask.link_id)}
                  disabled={isUnlinking || unlinkingId === linkedTask.link_id}
                  className="h-auto p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-50"
                  title="Unlink task"
                >
                  {unlinkingId === linkedTask.link_id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <X className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
